import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StatefulComponent } from '../../components/stateful/stateful.component';
import { VehicleService, VehicleWithImagesResponseDto, VehicleQueryParams } from '../../services/vehicle.service';
import { setContainerTypeAction } from '../../ngrx/actions/global.actions';
import * as GarageActions from '../../ngrx/actions/garage.actions';
import { selectVehicles, selectVehiclesLoading, selectVehiclesError, selectTotalPages } from '../../ngrx/app.state';
import { NgbCollapse, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { AddVehicleModalComponent } from '../../components/add-vehicle-modal/add-vehicle-modal.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { AuthorizeService } from '../../services/authorize.service';
import { Customer } from '../../models/Customer.model';

export type SortField = 'makeModelYear' | 'custFirstName' | 'custLastName' | 'custEmail' | 'vin';
export type SortDirection = 'asc' | 'desc';

// Single source of truth for "unfiltered": the URL parser, the active-filter
// badge and the one-shot auto-expand all compare against these.
const DEFAULT_SORT_FIELD: SortField = 'makeModelYear';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';
const DEFAULT_PAGE_SIZE = 10;

@Component({
  selector: 'app-my-garage',
  standalone: true,
  // NgbCollapse is standalone; NgbCollapseModule is not needed.
  imports: [CommonModule, RouterLink, FormsModule, PaginationComponent, NgbCollapse],
  templateUrl: './my-garage.component.html',
  styleUrl: './my-garage.component.scss'
})
export class MyGarageComponent extends StatefulComponent implements OnInit {
  vehicles: VehicleWithImagesResponseDto[] = [];
  selectedVehicleId: string | null = null;
  uploadingImage: boolean = false;
  imagePreview: string | null = null;
  selectedFile: File | null = null;
  uploadError: string | null = null;
  loading: boolean = false;
  error: string | undefined | null;

  // Sorting properties
  sortField: SortField = 'makeModelYear';
  sortDirection: SortDirection = 'asc';
  sortOptions: { value: string; label: string; }[] = [];

  // Customer filter (VIEW_ALL_GARAGES only): '' means all customers.
  customers: Customer[] = [];
  custUserId: string = '';

  // Pagination properties
  pageNumber: number = 1;
  pageSize: number = DEFAULT_PAGE_SIZE;
  totalPages: number = 1;

  // The filter panel starts collapsed at every width; ngOnInit opens it once if
  // the URL arrives carrying a non-default filter.
  filtersCollapsed: boolean = true;
  private filtersAutoExpandChecked: boolean = false;

  constructor(
    private vehicleService: VehicleService,
    private authorizeService: AuthorizeService,
    @Inject(NgbModal) private ngbModal: NgbModal,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super();
    this.updateSortOptions();
  }

  get hasViewAllGarages(): boolean {
    return this.permissions?.includes('VIEW_ALL_GARAGES') || false;
  }

  /** Count of settings differing from their defaults — drives the toggle's badge. */
  get activeFilterCount(): number {
    let count = 0;
    if (this.hasViewAllGarages && this.custUserId) count++;
    if (this.sortField !== DEFAULT_SORT_FIELD) count++;
    if (this.sortDirection !== DEFAULT_SORT_DIRECTION) count++;
    // Number(): pageSize round-trips through the URL as a string, so a bare
    // !== would report a filter as active while 10 is selected.
    if (Number(this.pageSize) !== DEFAULT_PAGE_SIZE) count++;
    return count;
  }

  /** One-line recap of the current view, shown while the panel is collapsed. */
  get filterSummary(): string {
    const parts: string[] = [];
    if (this.hasViewAllGarages) {
      const customer = this.customers.find(c => c.userId === this.custUserId);
      parts.push(customer ? this.getCustomerDisplayName(customer) : 'All customers');
    }
    parts.push(this.sortOptions.find(o => o.value === this.sortField)?.label ?? this.sortField);
    parts.push(this.sortDirection === 'asc' ? 'Ascending' : 'Descending');
    parts.push(`${this.pageSize} per page`);
    return parts.join(' · ');
  }

  ngOnInit() {
    this.dispatch(setContainerTypeAction({ containerType: 'normal' }));

    if (this.hasViewAllGarages) {
      this.loadCustomers();
    }

    // Subscribe to query params and initialize state from URL
    this.subs.push(this.route.queryParams.subscribe({
      next: (params) => {
        // Read params from URL or use defaults
        const pageNumber = params['pageNumber'] ? parseInt(params['pageNumber'], 10) : 1;
        const pageSize = params['pageSize'] ? parseInt(params['pageSize'], 10) : DEFAULT_PAGE_SIZE;
        const sortField = (params['sortField'] as SortField) || DEFAULT_SORT_FIELD;
        const sortDirection = (params['sortDirection'] as SortDirection) || DEFAULT_SORT_DIRECTION;

        // Update component state
        this.pageNumber = pageNumber;
        this.pageSize = pageSize;
        this.sortField = sortField;
        this.sortDirection = sortDirection;
        this.custUserId = this.hasViewAllGarages ? (params['custUserId'] || '') : '';

        // The panel starts collapsed, but a deep link carrying a filter has to
        // show the controls so they can be cleared — including when the result
        // set is empty. Read the freshly parsed locals rather than the fields:
        // pageSize here is a real number from parseInt. One-shot, because this
        // subscription re-fires on every updateUrlParams() round trip and must
        // not fight a user who collapsed the panel themselves.
        if (!this.filtersAutoExpandChecked) {
          this.filtersAutoExpandChecked = true;
          if (!!params['custUserId'] ||
              sortField !== DEFAULT_SORT_FIELD ||
              sortDirection !== DEFAULT_SORT_DIRECTION ||
              pageSize !== DEFAULT_PAGE_SIZE) {
            this.filtersCollapsed = false;
          }
        }

        // If URL params are missing, add defaults to URL (which will trigger this subscription again)
        const paramsExist = params['pageNumber'] && params['pageSize'] && params['sortField'] && params['sortDirection'];
        if (!paramsExist) {
          this.updateUrlParams();
          return; // Don't load yet, wait for URL update to trigger this subscription again
        }

        // Load vehicles with current params (only if params exist in URL)
        this.loadVehiclesFromState();
      }
    }));

    // Subscribe to vehicles from store
    this.subs.push(this.select(selectVehicles).subscribe({
      next: (vehicles) => {
        if (vehicles) {
          // Vehicles are already sorted by the backend
          this.vehicles = [...vehicles];
        }
      }
    }));

    // Subscribe to loading state
    this.subs.push(this.select(selectVehiclesLoading).subscribe({
      next: (loading) => {
        this.loading = loading;
      }
    }));

    // Subscribe to error state
    this.subs.push(this.select(selectVehiclesError).subscribe({
      next: (error) => {
        this.error = error;
      }
    }));

    // Subscribe to total pages
    this.subs.push(this.select(selectTotalPages).subscribe({
      next: (totalPages) => {
        this.totalPages = totalPages;
      }
    }));
  }

  /**
   * Load vehicles from the current component state (pageNumber, pageSize, sortField, sortDirection)
   */
  private loadVehiclesFromState(): void {
    // Map component sort field to backend sort field
    let backendSort: 'make-model-year' | 'firstName' | 'lastName' | 'email' | 'vin' = 'make-model-year';
    switch (this.sortField) {
      case 'makeModelYear':
        backendSort = 'make-model-year';
        break;
      case 'custFirstName':
        backendSort = 'firstName';
        break;
      case 'custLastName':
        backendSort = 'lastName';
        break;
      case 'custEmail':
        backendSort = 'email';
        break;
      case 'vin':
        backendSort = 'vin';
        break;
    }

    const params: VehicleQueryParams = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sort: backendSort,
      sortDir: this.sortDirection
    };
    // Staff can narrow the page to one customer; regular users are already
    // limited to their own vehicles by the API.
    if (this.custUserId) {
      params.custUserId = this.custUserId;
    }

    this.dispatch(GarageActions.loadVehicles({ params }));
  }

  /**
   * Update URL query params to reflect current state
   */
  private updateUrlParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        // null drops the param from the URL when no customer filter is active
        custUserId: this.custUserId || null
      },
      replaceUrl: true
    });
  }

  private loadCustomers(): void {
    this.subs.push(this.authorizeService.getCustomers().subscribe({
      next: (customers) => this.customers = customers,
      error: (error) => {
        console.error('[MyGarage] Failed to load customers:', error);
        this.setError('Failed to load customers for filtering.');
      }
    }));
  }

  onCustomerFilterChange(): void {
    // Reset to first page when the filter changes
    this.pageNumber = 1;
    this.updateUrlParams();
  }

  getCustomerDisplayName(customer: Customer): string {
    // Email is nullable (drop-off customers may only have a phone), so fall
    // back rather than rendering a literal "(null)" in the filter dropdown.
    const contact = customer.email ?? customer.phoneNumber;
    const name = `${customer.firstName} ${customer.lastName}`;
    return contact ? `${name} (${contact})` : name;
  }

  onImageSelected(event: Event, vehicleId: string): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files[0]) {
      const file = input.files[0];
      
      // Validate the file
      const validation = this.vehicleService.validateImageFile(file);
      if (!validation.valid) {
        this.uploadError = validation.error || 'Invalid file';
        return;
      }
      
      // Clear any previous error
      this.uploadError = null;
      this.selectedFile = file;
      this.selectedVehicleId = vehicleId;
      
      // Create preview
      // this.imagePreview = this.vehicleService.createImagePreview(file);
      
      // Auto-upload or wait for confirmation based on your UX preference
      this.uploadImage(vehicleId, file);
    }
  }

  uploadImage(vehicleId: string, file: File): void {
    this.uploadingImage = true;
    this.uploadError = null;
    
    this.subs.push(this.vehicleService.uploadVehicleImage(vehicleId, file).subscribe({
      next: (response) => {
        this.uploadingImage = false;
        this.clearImageSelection();
        // Reload vehicles to show the new image
        this.loadVehiclesFromState();
      },
      error: (error) => {
        console.error('Upload failed:', error);
        this.uploadError = 'Failed to upload image. Please try again.';
        this.uploadingImage = false;
      }
    }));
  }

  deleteImage(imageId: string): void {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    this.subs.push(this.vehicleService.deleteVehicleImage(imageId).subscribe({
      next: () => {
        this.loadVehiclesFromState();
      },
      error: (error) => {
        console.error('Delete failed:', error);
        this.setError('Failed to delete image. Please try again.');
      }
    }));
  }

  deleteVehicle(vehicleId: string): void {
    if (!confirm('Are you sure you want to delete this vehicle and all its images?')) {
      return;
    }

    this.subs.push(this.vehicleService.deleteVehicle(vehicleId).subscribe({
      next: () => {
        this.loadVehiclesFromState();
      },
      error: (error) => {
        console.error('Delete failed:', error);
        this.setError('Failed to delete vehicle. Please try again.');
      }
    }));
  }

  setError(error: string) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  clearImageSelection(): void {
    if (this.imagePreview) {
      this.vehicleService.revokeImagePreview(this.imagePreview);
      this.imagePreview = null;
    }
    this.selectedFile = null;
    this.selectedVehicleId = null;
    this.uploadError = null;
    
    // Clear file input
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  getVehicleDisplayName(vehicle: VehicleWithImagesResponseDto): string {
    const parts = [];
    if (vehicle.Year) parts.push(vehicle.Year);
    if (vehicle.Make) parts.push(vehicle.Make);
    if (vehicle.Model) parts.push(vehicle.Model);
    
    return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
  }

  getDefaultImage(): string {
    return this.vehicleService.getDefaultImage();
  }

  openAddVehicleModal(): void {
    const modalRef = this.ngbModal.open(AddVehicleModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.modalRef = modalRef;

    // Listen for vehicle added event
    this.subs.push(modalRef.componentInstance.vehicleAdded.subscribe(() => {
      this.loadVehiclesFromState();
    }));
  }

  onSortFieldChange(field: SortField): void {
    this.sortField = field;
    // Update URL, which will trigger reload via queryParams subscription
    this.updateUrlParams();
  }

  onSortDirectionChange(direction: SortDirection): void {
    this.sortDirection = direction;
    // Update URL, which will trigger reload via queryParams subscription
    this.updateUrlParams();
  }

  onPageChange(page: number): void {
    this.pageNumber = page;
    // Update URL, which will trigger reload via queryParams subscription
    this.updateUrlParams();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    // Reset to first page when changing page size
    this.pageNumber = 1;
    // Update URL, which will trigger reload via queryParams subscription
    this.updateUrlParams();
  }

  private updateSortOptions(): void {
    const hasViewAllGarages = this.permissions?.includes('VIEW_ALL_GARAGES');

    const allOptions = [
      { value: 'makeModelYear', label: 'Make / Model / Year' },
      { value: 'custFirstName', label: 'Customer First Name' },
      { value: 'custLastName', label: 'Customer Last Name' },
      { value: 'custEmail', label: 'Customer Email' },
      { value: 'vin', label: 'VIN' }
    ];

    if (hasViewAllGarages) {
      this.sortOptions = allOptions;
    } else {
      // only allow makeModelYear and vin for regular users
      this.sortOptions = allOptions.filter(option =>
        option.value === 'makeModelYear' || option.value === 'vin'
      );
    }
  }
}